import type { ReactNode } from 'react';

type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  className?: string;
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
  primary: 'bg-ondeck-100 dark:bg-ondeck-900/50 text-ondeck-800 dark:text-ondeck-300',
  success: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300',
  warning: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300',
  danger: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300',
  info: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300',
};

export function Badge({ children, variant = 'default', className = '' }: BadgeProps) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variantStyles[variant]} ${className}`}
    >
      {children}
    </span>
  );
}

// Stage-specific badges for the baseball metaphor
export type ContactStage = 'on_deck' | 'at_bat' | 'on_base' | 'scoring_position' | 'hall_of_fame' | 'bench';

const stageConfig: Record<ContactStage, { label: string; variant: BadgeVariant }> = {
  on_deck: { label: 'On Deck', variant: 'info' },
  at_bat: { label: 'At Bat', variant: 'warning' },
  on_base: { label: 'On Base', variant: 'success' },
  scoring_position: { label: 'Scoring Position', variant: 'warning' },
  hall_of_fame: { label: 'Hall of Fame', variant: 'primary' },
  bench: { label: 'Bench', variant: 'default' },
};

interface StageBadgeProps {
  stage: ContactStage;
  className?: string;
}

export function StageBadge({ stage, className = '' }: StageBadgeProps) {
  const config = stageConfig[stage] || stageConfig.on_deck;
  return (
    <Badge variant={config.variant} className={className}>
      {config.label}
    </Badge>
  );
}
